import Axios, { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "react-query";
import { poiKeys } from "src/query-keys";
import { APIError, Poi } from "src/types";

export type PoiMutationData = Omit<Poi, "id">;

/**
 * Deserialize the json format into a Javascript object
 */
function deserialize(data: Poi): Poi {
  return { ...data };
}

export function usePois(caseId: number | string, options?: UseQueryOptions<Poi[], AxiosResponse<APIError[]>, Poi[]>) {
  async function queryFn(caseId: number | string) {
    const result = await Axios.get(`/planning-api/cases/${caseId}/pois`);
    return result.data.map(deserialize);
  }
  return useQuery<Poi[], AxiosResponse<APIError[]>>(poiKeys.all(caseId), () => queryFn(caseId), options);
}

export function usePoi(
  caseId: number | string,
  poiId: number | string,
  options?: UseQueryOptions<Poi, AxiosError<APIError[]>, Poi>
) {
  async function queryFn(caseId: number | string, poiId: number | string) {
    const result = await Axios.get(`/planning-api/cases/${caseId}/pois/${poiId}`);
    return deserialize(result.data) as Poi;
  }
  return useQuery<Poi, AxiosError<APIError[]>>(poiKeys.detail(caseId, poiId), () => queryFn(caseId, poiId), options);
}

export function usePoiCreateMutation(caseId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(caseId: number | string, data: PoiMutationData) {
    const result = await Axios.post(`/planning-api/cases/${caseId}/pois`, data);
    return {
      id: result.data.id,
      ...data,
    };
  }

  return useMutation<Pick<Poi, "id">, AxiosError<APIError[]>, PoiMutationData, unknown>(
    (values: PoiMutationData) => mutationFn(caseId, values),
    {
      onSettled: () => queryClient.invalidateQueries(poiKeys.all(caseId)),
    }
  );
}

export function usePoiUpdateMutation(caseId: number | string, poiId: number | string) {
  const queryClient = useQueryClient();
  async function mutationFn(caseId: number | string, poiId: number | string, data: PoiMutationData): Promise<Poi> {
    await Axios.put(`/planning-api/cases/${caseId}/pois/${poiId}`, data);
    return {
      id: parseFloat(poiId.toString()),
      ...data,
    };
  }

  return useMutation<Poi, AxiosError<APIError[]>, PoiMutationData, unknown>(
    (values: PoiMutationData) => mutationFn(caseId, poiId, values),
    {
      onSuccess: async (updatedPoi) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(poiKeys.all(caseId));

        // Optimistically update to the new value
        queryClient.setQueryData(poiKeys.detail(caseId, poiId), () => updatedPoi);
        queryClient.setQueryData(poiKeys.all(caseId), (oldData: Poi[] | undefined) => {
          if (!oldData) return [];
          return [...oldData].map((item) => {
            if (item.id === poiId) return updatedPoi;
            return item;
          });
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries(poiKeys.all(caseId));
      },
    }
  );
}

export function usePoiDeleteMutation(caseId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(caseId: number | string, poiId: number | string): Promise<void> {
    return await Axios.delete(`/planning-api/cases/${caseId}/pois/${poiId}`);
  }

  return useMutation<void, AxiosError<APIError[]>, number | string, unknown>(
    (poiId: number | string) => mutationFn(caseId, poiId),
    {
      onSettled: (data, error, variables) => queryClient.invalidateQueries(poiKeys.all(caseId)),
    }
  );
}
